/**
 * OpeningExplorer component
 * 
 * Displays opening information for the current position:
 * - Opening name if position is in the book
 * - Available book moves with weights
 * - Theory and explanations
 * 
 * Educational component to help players learn chess openings.
 */

import React from 'react';
import { GameState } from '../../engine/types';
import { getOpeningInfo } from '../../engine/openingBook';
import './OpeningExplorer.css';

interface OpeningExplorerProps {
  gameState: GameState;
}

/**
 * OpeningExplorer component - Shows current opening name and book moves
 * 
 * Features:
 * - Displays opening name if in book
 * - Lists available book moves with weights
 * - Shows "Out of book" message when not in opening theory
 * - Compact card design matching other info panels
 * 
 * @param {GameState} gameState - Current chess position
 */
export const OpeningExplorer: React.FC<OpeningExplorerProps> = ({ gameState }) => {
  const openingInfo = getOpeningInfo(gameState);

  if (!openingInfo) {
    return (
      <div className="opening-explorer">
        <div className="opening-header">
          <h3>📖 Opening Explorer</h3>
        </div>
        <div className="opening-content">
          <p className="out-of-book">Out of book</p>
          <p className="out-of-book-desc">Position not found in opening theory</p>
        </div>
      </div>
    );
  }

  // Sort moves by weight (highest first)
  const sortedMoves = [...openingInfo.moves].sort((a, b) => b.weight - a.weight);

  return (
    <div className="opening-explorer">
      <div className="opening-header">
        <h3>📖 Opening Explorer</h3>
      </div>
      <div className="opening-content">
        <div className="opening-name-section">
          <span className="opening-label">Opening:</span>
          <span className="opening-name">{openingInfo.name}</span>
        </div>
        <div className="book-moves-section">
          <span className="book-moves-label">Book Moves:</span>
          <div className="book-moves-list">
            {sortedMoves.map((move, index) => (
              <div key={index} className="book-move-item">
                <span className="book-move-notation">{move.notation}</span>
                <span className="book-move-weight">{move.weight}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpeningExplorer;
